import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { SavedReportFilterOption } from '../models/filter.types'

/** Account+workspace-scoped cache for the `save_filters` workspace preference. Persisted so a
 *  hard reload reuses the last-fetched scope's records instead of re-requesting them on every
 *  mount of `useSavedReportFilters`. */
export const useSavedReportFiltersStore = defineStore(
  'savedReportFiltersStore',
  () => {
    const scopeKey = ref<string | null>(null)
    const filters = ref<SavedReportFilterOption[]>([])
    const fetchedAt = ref<number | null>(null)

    /** True once a fetch for this exact scope has completed — not merely started (`scopeKey`
     *  alone would race a second consumer mounting mid-fetch). */
    function ownsFreshScope(key: string): boolean {
      return scopeKey.value === key && fetchedAt.value !== null
    }

    function setFilters(key: string, next: readonly SavedReportFilterOption[]): void {
      scopeKey.value = key
      filters.value = [...next]
      fetchedAt.value = Date.now()
    }

    function replaceFilters(key: string, next: readonly SavedReportFilterOption[]): void {
      if (scopeKey.value !== key) return
      filters.value = [...next]
    }

    function resetState(): void {
      scopeKey.value = null
      filters.value = []
      fetchedAt.value = null
    }

    return { scopeKey, filters, fetchedAt, ownsFreshScope, setFilters, replaceFilters, resetState }
  },
  {
    persist: {
      key: 'savedReportFiltersStore',
      pick: ['scopeKey', 'filters', 'fetchedAt'],
    },
  },
)

export type SavedReportFiltersStore = ReturnType<typeof useSavedReportFiltersStore>
